// Front-end for the Solutions directory — the rail's industry checkboxes, the live
// search over the card grid, and the rail's Industries disclosure. Peer Vite entry —
// never imports the SCSS.
//
// Filtering is ONE pass over both controls rather than two independent ones, because
// they compose: a search term narrows the cards, the checkboxes narrow the industries,
// and a section left with nothing visible goes with its heading — otherwise the page
// keeps a "Bonus Hunting" label over an empty row.
//
// Both sides of the tier match are rendered from Industries.cs (see the note in
// Solutions.cshtml), so a checkbox can only ever name a section that exists.
//
// The capability tiles below the directory are plain links and need nothing here.

import { debounce } from 'lodash-es';

const results = document.querySelector<HTMLElement>('.sol-results');

if (results) {
    const field = document.querySelector<HTMLInputElement>('#sol-q');
    const checks = Array.from(document.querySelectorAll<HTMLInputElement>('.sol-rail input[type="checkbox"]'));
    const tiers = Array.from(results.querySelectorAll<HTMLElement>('.tier'));
    const empty = results.querySelector<HTMLElement>('.sol-empty');

    // A card's searchable text, read once — the markup is static, so re-reading it on
    // every keystroke would be pure waste.
    const cards = Array.from(results.querySelectorAll<HTMLElement>('.sol-card')).map((el) => ({
        el,
        text: (el.textContent ?? '').toLowerCase(),
    }));

    const apply = (): void => {
        const term = (field?.value ?? '').trim().toLowerCase();
        // No box ticked means "every tier", the same way the reference behaves.
        const picked = checks.filter((c) => c.checked).map((c) => c.dataset.tier);
        const wanted = (tier: string | undefined): boolean => picked.length === 0 || picked.includes(tier);

        let shown = 0;

        cards.forEach((card) => {
            const tier = card.el.closest<HTMLElement>('.tier')?.dataset.tier;
            const hit = wanted(tier) && (term === '' || card.text.includes(term));
            card.el.classList.toggle('d-none', !hit);
            if (hit) shown += 1;
        });

        // One grid per section, and the heading is inside the section too, so hiding
        // the section is all it takes — no separate pass over the grids or the labels.
        tiers.forEach((tier) => {
            tier.classList.toggle('d-none', tier.querySelectorAll('.sol-card:not(.d-none)').length === 0);
        });

        empty?.toggleAttribute('hidden', shown > 0);
    };

    // Debounced: apply() touches every card, and a fast typist would otherwise run it
    // once per keystroke.
    field?.addEventListener('input', debounce(apply, 120));
    checks.forEach((c) => c.addEventListener('change', apply));

    // /solutions/<industry> renders that industry's box already ticked (see the note in
    // Solutions.cshtml). No input or change event will ever fire for a box the server
    // ticked, so the opening filter pass has to be run here or the page would show every
    // industry under a checked filter.
    if (checks.some((c) => c.checked)) {
        apply();
    }
}

// The rail's Industries disclosure. Open in the markup and collapsed only from here, so
// with no JS the checkboxes are simply always visible rather than unreachable.
const disclosure = document.querySelector<HTMLButtonElement>('.sol-rail .panel-title button');
const checkList = document.querySelector<HTMLElement>('#sol-filters');

if (disclosure && checkList) {
    disclosure.addEventListener('click', () => {
        const open = disclosure.getAttribute('aria-expanded') !== 'true';
        disclosure.setAttribute('aria-expanded', String(open));
        checkList.toggleAttribute('hidden', !open);
    });
}
